import React from "react";
import { withTranslation, WithTranslation } from "react-i18next";

type ErrorBoundaryProps = {
  fallback?: React.ReactNode;
  children: React.ReactNode;
} & WithTranslation; // Extend ErrorBoundaryProps with WithTranslation to include 't' function

// Error boundary component to catch errors and display a fallback UI
// Error boundaries cannot be made as functional components
class ErrorBoundary extends React.Component<ErrorBoundaryProps> {
  state = {
    hasError: false,
    isReloading: false, // Add a new state to indicate reloading
  };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, isReloading: false };
  }

  componentDidCatch(error: Error, errorInfo: unknown) {
    try {
      console.error("ErrorBoundary caught an error: ", error, errorInfo);
      if (error.name === "ChunkLoadError") {
        const reloads = parseInt(
          localStorage.getItem("errorReloadCount") || "0",
          10
        );
        const lastReload = parseInt(
          localStorage.getItem("errorLastReload") || "0",
          10
        );
        const currentTime = Date.now();

        // Check if the last reload was more than 1 minute ago
        if (currentTime - lastReload > 60000) {
          localStorage.setItem("errorReloadCount", "1");
          localStorage.setItem("errorLastReload", currentTime.toString());
          this.setState({ isReloading: true });
          window.location.reload();
        } else if (reloads < 3) {
          // Limit to 3 reloads within a minute
          localStorage.setItem("errorReloadCount", (reloads + 1).toString());
          localStorage.setItem("errorLastReload", currentTime.toString());
          this.setState({ isReloading: true });
          window.location.reload();
        } else {
          console.error("Too many reloads due to ChunkLoadError");
        }
      }
    } catch (error) {
      console.error("Error caught in componentDidCatch method: ", error);
    }
  }

  render() {
    try {
      if (this.state.hasError) {
        // Show loading screen if isReloading is true
        if (this.state.isReloading) {
          return (
            //TODO - Add in a General / Custom Loader Component
            <div className="container m-auto">
              {this.props.t("Reloading...")}
            </div>
          );
        }

        // Fallback UI if an error is caught
        return (
          this.props.fallback || (
            <div className="container m-auto">
              <div className="mt-4 text-center text-3xl font-bold text-red-600">
                <h1>
                  {this.props.t("Something went wrong. Please contact support")}
                </h1>
              </div>
            </div>
          )
        );
      }

      return this.props.children;
    } catch (error) {
      // Catch any errors thrown within the render method itself
      console.error("Error caught in render method: ", error);
      return (
        <div className="container m-auto">
          <div className="mt-4 text-center text-3xl font-bold text-red-600">
            <h1>
              Something went wrong with error boundary. Please contact support
            </h1>
          </div>
        </div>
      );
    }
  }
}

// Assign the wrapped component to a named constant to avoid anonymous component issue
const TranslatedErrorBoundary = withTranslation()(ErrorBoundary);
export default TranslatedErrorBoundary;
